import { SelectorType } from "../../../components/shared/Selector/Selector";
import Selector from "../../../components/shared/Selector/Selector";
import styles from './CategorySelection.module.scss';
import BtButton from "../../bt-button/bt-button";
import { TrashIcon } from "../../../components/svgs";
import { uniqueId } from "lodash";
import { useTranslation } from "react-i18next";
import { formatAreaLabel } from "../../../util";

type SelectedCategory = {
  id: string,
  mainCategory: SelectorType & {subAreas: SelectorType[]} | null,
  subCategory: SelectorType | null,
};

type Props = {
  areas: (SelectorType & {subAreas: SelectorType[], _id: string, accountNumber?: string})[],
  currentProjectAreas?: {areaId: { name: string, slug: string }}[],
  selectedCategories: SelectedCategory[],
  setSelectedCategories: (fn: (prevState: SelectedCategory[]) => SelectedCategory[]) => void,
  showList: boolean,
}

const newArea = (): SelectedCategory => ({
  id: uniqueId(),
  mainCategory: null,
  subCategory: null,
});

const SingleCategorySelection = ({
  areas,
  currentProjectAreas = [],
  selectedCategories,
  setSelectedCategories,
  showList
}: Props) => {
  const [t] = useTranslation('projects', { keyPrefix: 'categorySelection' });
  const isSubAreaFieldMandatory = (mainArea: SelectorType & {subAreas: SelectorType[]} | null) => {
    return mainArea && currentProjectAreas?.find(area => area.areaId.name === mainArea.label);
  }

  const shouldSubAreaOptionBeDisabled = (mainCategory: SelectorType & {subAreas: SelectorType[]} | null, option: SelectorType | null) => {
    if (!mainCategory || !option) return false;
    return !!currentProjectAreas && !!(
      currentProjectAreas.find(area => area.areaId.slug === option.value) ||
      selectedCategories.find(_selectedCategory =>
        _selectedCategory.mainCategory?.value === mainCategory?.value &&
        _selectedCategory.subCategory?.value === option?.value
      )
    );
  }

  const handleMainCategoryChange = (value: SelectorType & {subAreas: SelectorType[]} | null, index: number) => {
    setSelectedCategories(prevState => {
      const newState = [...prevState];
      newState[index] = {
        ...newState[index],
        mainCategory: value,
        subCategory: null
      };
      return newState;
    });
  };

  const handleSubCategoryChange = (value: SelectorType | null, index: number) => {
    setSelectedCategories(prevState => {
      const newState = [...prevState];
      newState[index] = {
        ...newState[index],
        subCategory: value
      };
      return newState;
    });
  };

  const handleDeleteCategory = (index: number) => {
    setSelectedCategories(prevState => {
      const newState = [...prevState];
      newState.splice(index, 1);
      return newState;
    });
  };

  const categoriesToShow = !showList && selectedCategories.length === 0 ? [newArea()] : selectedCategories;

  return (
    <div className={styles.container}>
      {categoriesToShow.map((area, index) => {
        const getSelectedCategorySubAreas = () => area.mainCategory ? area.mainCategory.subAreas : [];

        const isLastArea = !showList || selectedCategories.length === 1;

        return (
          <div key={area.id} className={styles.areaContainer}>
            <Selector
              className={styles.areaSelector}
              label={t('label.mainCategory', 'Huvudkategori')}
              value={area.mainCategory}
              isClearable
              onChange={(value) => handleMainCategoryChange(value, index)}
              options={areas}
              placeholder={t('placeholder.mainCategory', 'Välj huvudkategori')}
              isSearchable={true}
              formatOptionLabel={(option) => formatAreaLabel(option.label, (option as { accountNumber?: string }).accountNumber)}
              getSearchString={(option) => formatAreaLabel(option.label, (option as { accountNumber?: string }).accountNumber)}
            />
            <Selector
              className={styles.areaSelector}
              label={t('label.subCategory', 'Underkategori')}
              isCreatable
              isClearable
              isDisabled={!area.mainCategory}
              value={area.subCategory}
              onChange={(value) => handleSubCategoryChange(value, index)}
              options={getSelectedCategorySubAreas()}
              placeholder={t('placeholder.subCategory', 'Välj eller skapa underkategori', { context: !isSubAreaFieldMandatory(area.mainCategory) ? 'optional' : null })}
              isOptionDisabled={option => shouldSubAreaOptionBeDisabled(area.mainCategory, option)}
              isSearchable={true}
            />
            <BtButton
              color='white'
              size='xxs'
              disabled={isLastArea}
              onClick={() => {
                if (!isLastArea) handleDeleteCategory(index);
              }}
              leftIcon={<TrashIcon size='14px' color='var(--gray-700)' />}
            />
          </div>
        )
      })}
    </div>
  );
};

export default SingleCategorySelection;
